import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../supabaseClient';
import './ResetPassword.css';

/**
 * ResetPassword Component
 *
 * Allows users to set a new password after clicking password reset link.
 * This page is accessed after email verification via AuthCallback.
 */
function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user has a valid session (from password reset email)
    checkSession();
  }, []);

  const checkSession = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      setHasSession(true);
    } else {
      setError('Invalid or expired password reset link. Please request a new one.');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) {
        throw updateError;
      }

      setSuccess('Password updated successfully! Redirecting to dashboard...');
      setPassword('');
      setConfirmPassword('');

      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);

    } catch (err) {
      console.error('Password reset error:', err);
      setError(err.message || 'Failed to reset password. Please try again.');
      setLoading(false);
    }
  };

  if (!hasSession) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-box">
          <div className="error-icon">⚠️</div>
          <h2>Invalid Reset Link</h2>
          <p className="error-message">{error}</p>
          <button
            className="reset-password-button"
            onClick={() => navigate('/login')}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-container">
      <div className="reset-password-box">
        <h2>Set New Password</h2>
        <p className="reset-password-subtitle">
          Enter your new password below. Make it strong!
        </p>

        <form onSubmit={handleResetPassword}>
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter new password"
              minLength={6}
              autoComplete="new-password"
              disabled={loading || success}
            />
            <span className="form-hint">Minimum 6 characters</span>
          </div>

          <div className="form-group">
            <label htmlFor="confirm-password">Confirm Password</label>
            <input
              type="password"
              id="confirm-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="Confirm new password"
              minLength={6}
              autoComplete="new-password"
              disabled={loading || success}
            />
          </div>

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <button
            type="submit"
            disabled={loading || success}
            className="reset-password-button"
          >
            {loading ? 'Updating Password...' : success ? 'Success!' : 'Update Password'}
          </button>
        </form>

        <div className="form-footer">
          <button
            type="button"
            className="link-button"
            onClick={() => navigate('/login')}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
